import React, { useEffect, useRef, useState } from "react";
import { useImportApi } from "./import/importApi";
import { toastApiError, useMountedRef } from "./import/common";
import "./import/Import.css";

// "What next?" after a scrub: take the red list into Add Contacts or the green
// list into the new-lead importer — straight from the stored scrub result
// (services/scrub_results, no download + re-upload), or from the user's own
// reviewed copy of that list. Yellow stays download-only: it needs a person
// to decide which side each row belongs on.

const BUCKETS = [
	{
		key: "red",
		count: "HIGH",
		title: "Already in Iris",
		color: "#c53030",
		action: "Add new contacts to these leads",
		kind: "contacts",
		hint: "Adds the list's contacts to the leads they matched. Contacts already on a lead are skipped.",
	},
	{
		key: "yellow",
		count: "MEDIUM",
		title: "Needs review",
		color: "#b7791f",
		hint: "Not imported as-is. Review it, then upload your reviewed rows as a red or green list.",
	},
	{
		key: "green",
		count: "LOW",
		title: "New companies",
		color: "#276749",
		action: "Import as new leads",
		kind: "leads",
		hint: "Creates a new lead for each company, with its contacts.",
	},
];

export default function ScrubNextSteps({ result, onHandoff }) {
	const api = useImportApi();
	const mountedRef = useMountedRef();
	const fileRefs = useRef({});
	const [access, setAccess] = useState("checking"); // "checking" | "ok" | "none"
	const [source, setSource] = useState({ red: "scrub", green: "scrub" });
	const [busy, setBusy] = useState(null);

	const resultId = result?.scrub_result_id || null;
	const counts = result?.notification?.counts || {};
	const scrubName = result?.uploaded_filename || "this scrub";

	useEffect(() => {
		let cancelled = false;
		api
			.getMe({ quiet: true })
			.then(() => !cancelled && setAccess("ok"))
			.catch(() => !cancelled && setAccess("none"));
		return () => {
			cancelled = true;
		};
	}, [api]);

	const go = async (bucket) => {
		const useOwn = source[bucket.key] === "own" || !resultId;
		if (useOwn) {
			fileRefs.current[bucket.key]?.click();
			return;
		}
		setBusy(bucket.key);
		try {
			const staged = await api.stageScrubBucket(resultId, bucket.key);
			if (mountedRef.current) onHandoff(bucket.kind, staged, staged.filename);
		} catch (err) {
			toastApiError(err, `Could not load the ${bucket.key} list`);
		} finally {
			if (mountedRef.current) setBusy(null);
		}
	};

	const onOwnFile = async (bucket, e) => {
		const file = e.target.files && e.target.files[0];
		e.target.value = "";
		if (!file) return;
		setBusy(bucket.key);
		try {
			const staged = await api.uploadCsv(file);
			if (mountedRef.current) onHandoff(bucket.kind, staged, file.name);
		} catch (err) {
			toastApiError(err, "Upload failed");
		} finally {
			if (mountedRef.current) setBusy(null);
		}
	};

	const download = async (bucket) => {
		try {
			await api.downloadScrubBucket(resultId, bucket.key);
		} catch (err) {
			toastApiError(err, `Could not download the ${bucket.key} list`);
		}
	};

	return (
		<div className="importCard" style={{ marginTop: 20 }}>
			<h2>What next?</h2>
			<p className="importMuted">
				Results for <b>{scrubName}</b>. Pick what to do with each list. You can use the scrub's list
				as-is, or upload your own reviewed copy if you changed it.
			</p>
			{access === "none" && (
				<div className="importError">
					Importing into Iris needs import access. Ask Brian to add you. You can still download the
					lists below.
				</div>
			)}
			{!resultId && (
				<p className="importMuted">
					This scrub's lists weren't stored on the server, so use the ZIP download and upload your
					reviewed list below.
				</p>
			)}
			<div style={{ display: "grid", gap: 12 }}>
				{BUCKETS.map((b) => {
					const n = counts[b.count] ?? 0;
					const canImport = b.kind && access === "ok" && n > 0;
					return (
						<div
							key={b.key}
							style={{
								border: "1px solid #e2e8f0",
								borderLeft: `4px solid ${b.color}`,
								borderRadius: 6,
								padding: "10px 14px",
							}}>
							<div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
								<div>
									<b style={{ color: b.color, textTransform: "capitalize" }}>{b.key}</b>
									{" · "}
									<b>{n}</b> {b.title.toLowerCase()}
									<div className="importMuted" style={{ fontSize: 13 }}>
										{b.hint}
									</div>
								</div>
								<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
									{resultId && n > 0 && (
										<button type="button" className="importBtn secondary small" onClick={() => download(b)}>
											Download
										</button>
									)}
									{b.kind && (
										<button
											type="button"
											className="importBtn small"
											disabled={!canImport || busy !== null}
											onClick={() => go(b)}>
											{busy === b.key ? "Loading…" : b.action}
										</button>
									)}
								</div>
							</div>
							{b.kind && canImport && (
								<div style={{ marginTop: 8, fontSize: 13 }}>
									{resultId && (
										<label style={{ marginRight: 16 }}>
											<input
												type="radio"
												name={`src-${b.key}`}
												checked={source[b.key] === "scrub"}
												onChange={() => setSource((s) => ({ ...s, [b.key]: "scrub" }))}
											/>{" "}
											Use the scrub's list
										</label>
									)}
									<label>
										<input
											type="radio"
											name={`src-${b.key}`}
											checked={source[b.key] === "own" || !resultId}
											onChange={() => setSource((s) => ({ ...s, [b.key]: "own" }))}
										/>{" "}
										Upload my reviewed version (.csv or .xlsx)
									</label>
									<input
										ref={(el) => {
											fileRefs.current[b.key] = el;
										}}
										type="file"
										accept=".csv,.xlsx"
										style={{ display: "none" }}
										onChange={(e) => onOwnFile(b, e)}
									/>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}
